import React, { useState } from 'react';
import Block from './Block';
import PillTag from './PillTag';
import { PredictionsTableBody } from './PredictionsTable';
import { mapeTone } from './metricTone';

// Backend's `default_storage.url()` returns paths like `/media/...`. Resolve
// those against the configured API origin so the link works from the React
// dev server (localhost:3000) which would otherwise 404 looking up its own
// origin. Mirrors the helper in PredictionsTable.jsx.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

function resolveDownloadUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${API_ORIGIN}${url}`;
  return url;
}

const formatNumber = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
};

const formatDelta = (curr, prev) => {
  if (prev === undefined || prev === null || curr === undefined || curr === null) return null;
  if (Math.abs(prev) < 1e-9) return null;
  const pct = ((curr - prev) / Math.abs(prev)) * 100;
  return { value: pct, sign: pct >= 0 ? '+' : '' };
};

// MAPE arrives here as a fraction (0.0–1.0); mapeTone works on percents.
const accuracyTone = (acc) => mapeTone(acc * 100);

const ForecastChart = ({ data, height = 140 }) => {
  if (!data || data.length === 0) return null;

  const W = 520;
  const H = height;
  const padX = 28;
  const padTop = 18;
  const padBottom = 30;
  const innerH = H - padTop - padBottom;
  const innerW = W - padX * 2;

  const values = data.map((d) => Number(d.value || 0));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || Math.max(Math.abs(max), 1);
  // Add 10% padding above/below so the curve doesn't kiss the chart bounds.
  const yMax = max + range * 0.15;
  const yMin = min - range * 0.15;
  const yRange = yMax - yMin || 1;

  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points = data.map((d, i) => {
    const x = padX + i * stepX;
    const y = padTop + innerH - ((Number(d.value || 0) - yMin) / yRange) * innerH;
    return { x, y, ...d };
  });

  const splitIdx = points.findIndex((p) => p.predicted);
  const actualPoints = splitIdx === -1 ? points : points.slice(0, splitIdx + 1).slice(0, -1).concat(points[splitIdx - 1] ?? []);
  // Actual segment is everything up to (but not including) the predicted point.
  const actualPath = (splitIdx === -1 ? points : points.slice(0, splitIdx))
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ');
  // Forecast segment is the dashed line from the last actual to the predicted point.
  const forecastPath = splitIdx > 0
    ? `M${points[splitIdx - 1].x},${points[splitIdx - 1].y} L${points[splitIdx].x},${points[splitIdx].y}`
    : '';

  // Filled area under the actual line for visual weight.
  const baseline = padTop + innerH;
  const areaPath = (splitIdx === -1 ? points : points.slice(0, splitIdx)).length > 1
    ? `${actualPath} L${(splitIdx === -1 ? points : points.slice(0, splitIdx)).slice(-1)[0].x},${baseline} L${points[0].x},${baseline} Z`
    : '';

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ overflow: 'visible' }}>
      {/* Subtle horizontal grid */}
      {[0.25, 0.5, 0.75].map((t) => (
        <line
          key={t}
          x1={padX} x2={W - padX}
          y1={padTop + innerH * t} y2={padTop + innerH * t}
          stroke="var(--border)" strokeWidth="1"
        />
      ))}

      {/* Flat band under the historical curve */}
      {areaPath && <path d={areaPath} fill="var(--chart-band)" stroke="none" />}

      {/* Historical line */}
      <path d={actualPath} fill="none" stroke="var(--chart-1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Forecast (dashed) link */}
      {forecastPath && (
        <path d={forecastPath} fill="none" stroke="var(--chart-2)" strokeWidth="2"
              strokeDasharray="4 4" strokeLinecap="round" />
      )}

      {/* Historical dots */}
      {points.map((p, i) => (!p.predicted ? (
        <circle key={`a-${i}`} cx={p.x} cy={p.y} r="3"
                fill="var(--card)" stroke="var(--chart-1)" strokeWidth="2" />
      ) : null))}

      {points.map((p, i) => (p.predicted ? (
        <circle key={`f-${i}`} cx={p.x} cy={p.y} r="4" fill="var(--chart-2)" stroke="var(--card)" strokeWidth="2" />
      ) : null))}

      {/* Period labels */}
      {points.map((p, i) => (
        <text
          key={`t-${i}`}
          x={p.x} y={H - 8}
          textAnchor="middle"
          fontSize="10.5"
          fill={p.predicted ? 'var(--foreground)' : 'var(--muted-foreground)'}
          fontWeight={p.predicted ? 600 : 500}
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
};

// Two-way segmented control (Table | Chart). Kept local so the forecast block
// stays self-contained; mirrors the pill styling used elsewhere in the chat.
const ViewToggle = ({ value, onChange, options }) => (
  <div style={{
    display: 'inline-flex', gap: 2, padding: 2, borderRadius: 8,
    background: 'var(--muted)',
    border: '1px solid var(--border)',
  }}>
    {options.map((opt) => {
      const active = opt.key === value;
      return (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          style={{
            padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
            background: active ? 'var(--card)' : 'transparent',
            color: active ? 'var(--foreground)' : 'var(--muted-foreground)',
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const ForecastBlock = ({ blockData = {} }) => {
  const data = blockData.data || [];
  const accuracy = Math.max(0, Math.min(1, Number(blockData.accuracy ?? 0)));
  const tone = accuracyTone(accuracy);

  // Per-entity predictions table (primary view); the chart is a switchable
  // secondary view. Older payloads carry only chart `data`, so fall back to
  // chart-only in that case.
  const tableRows = blockData.predictions || [];
  const tableColumns = blockData.columns || [];
  const hasTable = tableRows.length > 0 && tableColumns.length > 0;
  const hasChart = data.length > 0;
  const [view, setView] = useState(hasTable ? 'table' : 'chart');
  const activeView = hasTable ? view : 'chart';

  // Headline forecast: prefer explicit forecast_value, fall back to last predicted point.
  const forecastValue = blockData.forecast_value ?? data.find((d) => d.predicted)?.value ?? null;
  const lastActual = [...data].reverse().find((d) => !d.predicted)?.value ?? null;
  const delta = formatDelta(forecastValue, lastActual);

  const periodLabel = blockData.forecast_period
    || data.find((d) => d.predicted)?.label
    || 'next period';

  const nEntities = blockData.n_entities;

  return (
    <Block
      icon="trending-up"
      title={blockData.title || 'Forecast'}
      badge={
        <PillTag tone={tone} dot>
          {accuracy > 0 ? `±${(accuracy * 100).toFixed(0)}% MAPE` : 'No holdout MAPE'}
        </PillTag>
      }
      footer={resolveDownloadUrl(blockData.download_url) ? (
        <a href={resolveDownloadUrl(blockData.download_url)} download
           style={{ color: 'var(--foreground)', textDecoration: 'none', fontWeight: 500 }}>
          Download per-entity predictions ↓
        </a>
      ) : null}
    >
      {/* Headline */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--fg-subtle)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 500, marginBottom: 4 }}>
            {periodLabel}
          </div>
          <div style={{ fontSize: 30, fontWeight: 600, color: 'var(--foreground)', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {formatNumber(forecastValue)}
          </div>
        </div>
        {delta && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 12, fontWeight: 500,
            color: delta.value >= 0 ? 'var(--green)' : 'var(--error-500)',
          }}>
            <span>{delta.sign}{delta.value.toFixed(1)}%</span>
            <span style={{ color: 'var(--fg-subtle)', fontWeight: 400 }}>vs last</span>
          </div>
        )}
        <div style={{ flex: 1 }} />
        {nEntities ? (
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
            avg across {nEntities.toLocaleString()} {nEntities === 1 ? 'entity' : 'entities'}
          </div>
        ) : null}
      </div>

      {hasTable && hasChart && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <ViewToggle
            value={activeView}
            onChange={setView}
            options={[{ key: 'table', label: 'Table' }, { key: 'chart', label: 'Chart' }]}
          />
          {activeView === 'table' && blockData.table_note && (
            <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{blockData.table_note}</span>
          )}
        </div>
      )}

      {activeView === 'table' && hasTable && (
        <PredictionsTableBody predictions={tableRows} columns={tableColumns} inset={false} />
      )}
      {activeView === 'chart' && hasChart && <ForecastChart data={data} />}

      {blockData.summary && (
        <div style={{
          marginTop: 16, padding: '10px 12px',
          background: 'var(--secondary)', border: '1px solid var(--border)',
          borderRadius: 4, fontSize: 12.5, lineHeight: 1.5, color: 'var(--fg)',
        }}>
          <strong style={{ color: 'var(--foreground)' }}>What this means: </strong>
          {blockData.summary}
        </div>
      )}
    </Block>
  );
};

export default ForecastBlock;
